import { useEffect, useState } from "react";
import { Users, CreditCard, Sparkles, Loader2, Building2 } from "lucide-react";
import { adminUsers } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const PLAN_LABEL = { free: "Starter", single: "Single Authority", all: "All Authorities", pro: "Pro (legacy)", team: "Team (legacy)" };

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="font-display text-3xl font-bold text-zinc-100">{value}</div>
      {sub && <div className="font-mono text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role !== "admin") { nav("/"); return; }
    adminUsers().then(setData).catch((e) => setErr(e?.response?.data?.detail || "Failed to load users"));
  }, [user, nav]);

  if (err) return <div className="p-8 font-mono text-sm text-red-400" data-testid="admin-users-error">{err}</div>;
  if (!data) return <div className="p-8 flex items-center gap-2 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading users…</div>;

  const paying = (data.by_plan?.single || 0) + (data.by_plan?.all || 0);

  return (
    <div className="p-8 space-y-8 max-w-6xl" data-testid="admin-users-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Users</h1>
        <p className="font-mono text-[11px] text-zinc-500 mt-1">Live from this environment's database.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total users" value={data.total} sub={`${data.by_provider?.google || 0} Google · ${data.by_provider?.password || 0} email`} />
        <Stat icon={Building2} label="On a paid plan" value={paying} sub={`${data.by_plan?.single || 0} Single · ${data.by_plan?.all || 0} All`} />
        <Stat icon={CreditCard} label="Subscriptions" value={data.active_subscriptions} sub="with a Stripe customer" />
        <Stat icon={Sparkles} label="Beta grants" value={data.beta_grants} sub="365-day comp access" />
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#101010] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="admin-users-table">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3">Email</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Seats</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Auth</th><th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              {data.users.map((u, i) => (
                <tr key={u.email + i} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                  <td className="px-4 py-2.5 text-zinc-200">{u.email}{u.beta && <span className="ml-2 text-[9px] px-1 rounded-sm bg-amber-600/20 text-amber-400">BETA</span>}</td>
                  <td className="px-4 py-2.5"><span className={u.plan === "all" ? "text-emerald-400" : u.plan === "single" ? "text-blue-400" : "text-zinc-400"}>{PLAN_LABEL[u.plan] || u.plan}</span></td>
                  <td className="px-4 py-2.5 text-zinc-400">{u.seats}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{u.subscription_status || (u.plan === "free" ? "—" : "active")}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{u.provider}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
